//
// Copyright © 2024 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { Class, Doc, Mixin, Permission, Ref, SpaceType, SpaceTypeDescriptor } from '@hcengineering/core'
import { NotificationGroup, NotificationType } from '@hcengineering/notification'
import type { Asset, Metadata, Plugin, Resource } from '@hcengineering/platform'
import { IntlString, plugin } from '@hcengineering/platform'
import type { AnyComponent, Location, ResolvedLocation } from '@hcengineering/ui/src/types'
import { Action } from '@hcengineering/view'
import { Document, DocumentSnapshot, PublicSite, Teamspace } from './types'

/**
 * @public
 */
export const documentId = 'document' as Plugin

/**
 * @public
 */
export const documentPlugin = plugin(documentId, {
  class: {
    Document: '' as Ref<Class<Document>>,
    DocumentSnapshot: '' as Ref<Class<DocumentSnapshot>>,
    Teamspace: '' as Ref<Class<Teamspace>>,
    PublicSite: '' as Ref<Class<PublicSite>>
  },
  mixin: {
    DefaultTeamspaceTypeData: '' as Ref<Mixin<Teamspace>>
  },
  component: {
    CreateDocument: '' as AnyComponent,
    DocumentSearchIcon: '' as AnyComponent,
    PublicSiteApp: '' as AnyComponent,
    DocumentTrash: '' as AnyComponent
  },
  action: {
    CreateChildDocument: '' as Ref<Action>,
    CreateDocument: '' as Ref<Action>,
    EditTeamspace: '' as Ref<Action>,
    // Typed over Document, like the identifiers of the other Document-targeted actions in
    // models/controlled-documents: `Action<T>.query` is a `DocumentQuery<T>`, which is
    // invariant in T, so a `Ref<Action>` (T = Doc) cannot name an action whose query is over
    // Document. Only the `createAction` call in models/document refers to this identifier.
    EditPublicPageSeo: '' as Ref<Action<Document, any>>,
    PublishTeamspace: '' as Ref<Action>,
    TrashDocument: '' as Ref<Action>,
    RestoreDocument: '' as Ref<Action>,
    DeleteDocumentPermanently: '' as Ref<Action>
  },
  icon: {
    Document: '' as Asset,
    DocumentApplication: '' as Asset,
    Add: '' as Asset,
    Teamspace: '' as Asset,
    References: '' as Asset,
    History: '' as Asset,
    Star: '' as Asset,
    Starred: '' as Asset,
    Lock: '' as Asset,
    Unlock: '' as Asset,
    PublicSite: '' as Asset
  },
  metadata: {
    /**
     * Url of the workspace serving public teamspace sites on this host.
     * Public sites are disabled when it is not set.
     */
    PublicSiteWorkspace: '' as Metadata<string>
  },
  app: {
    Documents: '' as Ref<Doc>
  },
  resolver: {
    Location: '' as Resource<(loc: Location) => Promise<ResolvedLocation | undefined>>
  },
  string: {
    ConfigLabel: '' as IntlString,
    CreateDocument: '' as IntlString,
    Documents: '' as IntlString,
    ForbidCreateTeamspacePermission: '' as IntlString,
    ForbidCreateTeamspacePermissionDescription: '' as IntlString,
    PublishAsSite: '' as IntlString,
    EditPublicPageSeo: '' as IntlString,
    SiteHomePage: '' as IntlString,
    Trash: '' as IntlString,
    MoveToTrash: '' as IntlString,
    RestoreFromTrash: '' as IntlString,
    DeletePermanently: '' as IntlString,
    EmptyTrash: '' as IntlString,
    TrashIsEmpty: '' as IntlString,
    TrashRetentionHint: '' as IntlString,
    DeletedOn: '' as IntlString,
    DeletedBy: '' as IntlString,
    ExpiresOn: '' as IntlString,
    ExpiresToday: '' as IntlString,
    TrashedDocument: '' as IntlString,
    TrashedDocumentHint: '' as IntlString,
    ConfirmEmptyTrash: '' as IntlString,
    ConfirmEmptyTrashHint: '' as IntlString,
    ConfirmDeletePermanently: '' as IntlString,
    ConfirmDeletePermanentlyHint: '' as IntlString,
    ConfirmTrashDocument: '' as IntlString,
    ConfirmTrashDocumentHint: '' as IntlString,
    RestoredToTopLevel: '' as IntlString,
    ChildDocumentsCount: '' as IntlString,
    ForbidTrashDocumentPermission: '' as IntlString,
    ForbidTrashDocumentPermissionDescription: '' as IntlString
  },
  ids: {
    NoParent: '' as Ref<Document>,
    DocumentNotificationGroup: '' as Ref<NotificationGroup>,
    ContentNotification: '' as Ref<NotificationType>
  },
  descriptor: {
    TeamspaceType: '' as Ref<SpaceTypeDescriptor>
  },
  spaceType: {
    DefaultTeamspaceType: '' as Ref<SpaceType>
  },
  permission: {
    ForbidCreateTeamspace: '' as Ref<Permission>,
    ForbidTrashDocument: '' as Ref<Permission>
  }
})

export default documentPlugin
